import type { ReactNode } from 'react';
import { Input } from '@/components/form/Input';
import { Button } from '@/components/ui/Button';
import { dayName, inr, r2 } from './voucherFormat';

/** Pieces every voucher form shares, as the prototype draws them. */

/** Section heading ("Particulars", "Bank Allocation", "Posting"). */
export function Sec({ children }: { children: ReactNode }) {
  return <div className="mb-2 mt-4 border-b border-[var(--border-color)] pb-1 text-[11px] font-extrabold uppercase tracking-wider text-[var(--text-muted)]">{children}</div>;
}

export function F({ label, children, className = '' }: { label: ReactNode; children: ReactNode; className?: string }) {
  return (
    <label className={`block ${className}`}>
      <span className="mb-1 block text-[11px] font-bold uppercase tracking-wide text-[var(--text-secondary)]">{label}</span>
      {children}
    </label>
  );
}

export function DateField({ value, max, onChange }: { value: string; max: string; onChange: (v: string) => void }) {
  return (
    <F label={<>Date <span className="font-semibold normal-case tracking-normal text-[var(--text-muted)]">· {dayName(value)}</span></>}>
      <Input type="date" value={value} max={max} onChange={(e) => onChange(e.target.value)} />
    </F>
  );
}

/** Read-only box (Cur Bal, Amount in Bank Allocation). */
export function ReadBox({ value }: { value: string }) {
  return <Input disabled value={value} className="text-right font-mono" />;
}

export type Chip = 'g' | 'r' | 'b' | 'm' | 'o';
const CHIP: Record<Chip, string> = {
  g: 'bg-success/15 text-success border-success/30',
  r: 'bg-danger/15 text-danger border-danger/30',
  b: 'bg-info/15 text-info border-info/30',
  m: 'bg-white/[0.06] text-[var(--text-secondary)] border-white/10',
  o: 'bg-warning/15 text-warning border-warning/30',
};
export function ChipTag({ tone, children, title }: { tone: Chip; children: ReactNode; title?: string }) {
  return <span title={title} className={`inline-flex items-center whitespace-nowrap rounded-full border px-2 py-0.5 text-[10px] font-bold ${CHIP[tone]}`}>{children}</span>;
}

/** Modal title: "Receipt No. RV/26-27/0013" with its chip. */
export function FormTitle({ title, no, chip, extra }: { title: string; no: string; chip: ReactNode; extra?: ReactNode }) {
  return (
    <span className="flex flex-wrap items-center gap-2">
      <span>{title} <span className="font-mono text-xs font-semibold text-[var(--text-secondary)]">No. {no}</span></span>
      {chip}
      {extra}
    </span>
  );
}

/** What stops the save (✕) and what only warns (!). */
export function Msgs({ block, warn = [] }: { block: string[]; warn?: string[] }) {
  if (!block.length && !warn.length) return null;
  return (
    <div className="mt-3 space-y-1">
      {block.map((m, i) => <div key={`b${i}`} className="rounded-md border border-danger/30 bg-danger/10 px-2.5 py-1.5 text-xs text-danger"><b>✕</b> {m}</div>)}
      {warn.map((m, i) => <div key={`w${i}`} className="rounded-md border border-warning/30 bg-warning/10 px-2.5 py-1.5 text-xs text-warning"><b>!</b> {m}</div>)}
    </div>
  );
}

/** The Dr / Cr lines this voucher will post, with totals. */
export interface PostLine { l: string; a: number; sub?: string[] }
export function Posting({ lines }: { lines: PostLine[] }) {
  const show = lines.filter((x) => x.l && Math.abs(x.a) > 0.004);
  const dr = r2(show.filter((x) => x.a > 0).reduce((s, x) => s + x.a, 0));
  const cr = r2(show.filter((x) => x.a < 0).reduce((s, x) => s - x.a, 0));
  return (
    <div className="overflow-x-auto rounded-lg border border-[var(--border-color)]">
      <table className="w-full text-xs">
        <thead className="bg-white/[0.03] text-[var(--text-muted)]">
          <tr><th className="px-2 py-1.5 text-left">Particulars</th><th className="px-2 py-1.5 text-right">Debit</th><th className="px-2 py-1.5 text-right">Credit</th></tr>
        </thead>
        <tbody>
          {show.map((x, i) => (
            <tr key={i} className="border-t border-[var(--border-color)] align-top">
              <td className="px-2 py-1.5">
                <b>{x.a > 0 ? 'Dr' : 'Cr'}</b>&nbsp; {x.l}
                {(x.sub ?? []).map((t, j) => <div key={j} className="pl-6 text-[11px] text-[var(--text-muted)]">{t}</div>)}
              </td>
              <td className="px-2 py-1.5 text-right font-mono">{x.a > 0 ? inr(x.a) : ''}</td>
              <td className="px-2 py-1.5 text-right font-mono">{x.a < 0 ? inr(-x.a) : ''}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="border-t border-[var(--border-color)] font-extrabold">
            <td className="px-2 py-1.5 text-right">Total {Math.abs(dr - cr) < 0.005 ? <span className="text-success">✓ balanced</span> : <span className="text-warning">Difference {inr(dr - cr)}</span>}</td>
            <td className="px-2 py-1.5 text-right font-mono">{inr(dr)}</td>
            <td className="px-2 py-1.5 text-right font-mono">{inr(cr)}</td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

/** Footer: totals on the left, the altering marker, Cancel and Save. */
export function FormFooter({ left, version, saveLabel, blocked, saving, onClose, onSave }: {
  left: ReactNode; version?: number; saveLabel: string; blocked: boolean; saving: boolean; onClose: () => void; onSave: () => void;
}) {
  return (
    <div className="flex w-full flex-wrap items-center justify-end gap-2">
      <span className="mr-auto text-xs text-[var(--text-secondary)]">{left}</span>
      {version != null && <ChipTag tone="o" title="Saving replaces the voucher; the old one stays in the Edit Log">Altering · saves as version {version}</ChipTag>}
      <Button variant="glass" onClick={onClose}>Cancel</Button>
      <Button variant="primary" disabled={blocked || saving} onClick={onSave}>{saving ? 'Saving…' : saveLabel}</Button>
    </div>
  );
}

/** Small bordered button ("＋ Add party", "⚡ Oldest bills first"). */
export function SmBtn({ children, onClick, title, disabled }: { children: ReactNode; onClick: () => void; title?: string; disabled?: boolean }) {
  return (
    <button type="button" title={title} disabled={disabled} onClick={onClick}
      className="inline-flex h-7 items-center gap-1 rounded-md border border-[var(--border-color)] bg-[var(--card-bg)] px-2.5 text-[11px] font-bold text-[var(--text-secondary)] transition hover:border-accent hover:text-accent disabled:cursor-not-allowed disabled:opacity-50">
      {children}
    </button>
  );
}

/** Row × button. */
export function XBtn({ onClick, title }: { onClick: () => void; title: string }) {
  return <button type="button" title={title} onClick={onClick} className="rounded px-1.5 py-0.5 text-xs font-bold text-danger hover:bg-danger/10">✕</button>;
}
